import { useMutation } from "@apollo/client";
import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";

import TemplateListItem from "./TemplateListItem";
import { createNewTemplate } from "./utils";
import { CREATE_AUTO_TEMPLATE } from "../../graphql/mutations/AutoTemplates";
import { usePremiumFeature } from "../../hooks";

const AutoTemplatesList = ({
  title,
  subtitle,
  templates,
  templateType,
  onTemplateClick,
  onFavouriteToggle,
  isModal = false,
  dataTestId,
}) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const { enableAdaptiveTemplates } = useFlags();
  const { PremiumFeatureGate, UserPermissions } = usePremiumFeature();

  const [createAutoTemplate] = useMutation(CREATE_AUTO_TEMPLATE);

  let filteredTemplates = templates
    .filter((template) =>
      templateType === "FAVOURITES"
        ? template.isFavourite
        : template.templateType === templateType || !templateType,
    )
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  const handleCreateNewTemplate = async () => {
    const templateUuid = await createNewTemplate(
      templateType,
      true,
      createAutoTemplate,
    );
    navigate(`/templates/edit/${templateUuid}?isNew=true`);
  };

  if (
    (isModal && filteredTemplates.length === 0) ||
    (templateType === "FAVOURITES" && filteredTemplates.length === 0)
  ) {
    return null;
  }

  // Only show SCRIBENOTE_DEFAULTS if feature flag is not enabled
  // This is important for the reprocessing 'change template' modal
  if (
    !enableAdaptiveTemplates &&
    templateType !== "SCRIBENOTE_DEFAULT"
  ) {
    return null;
  }

  return (
    <div data-testid={dataTestId}>
      {title && (
        <div className="mb-2">
          <h3 className="text-xl font-semibold">{title}</h3>
          {subtitle && (
            <p className="text-xs font-normal text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="grid gap-2 rounded-xl"
          style={{
            gridTemplateColumns:
              "repeat(auto-fill, minmax(250px, 1fr))",
          }}
        >
          {filteredTemplates.length > 0 &&
            filteredTemplates.map((template) => {
              const templateItem = (
                <TemplateListItem
                  dataTestId={"template-item-" + template.name}
                  key={template.uuid || template.value}
                  icon={
                    template.templateType === "SOAP"
                      ? faSoapNote
                      : faFile
                  }
                  template={template}
                  onClick={() => onTemplateClick(template)}
                  onFavouriteToggle={
                    onFavouriteToggle
                      ? () => onFavouriteToggle(template)
                      : undefined
                  }
                />
              );

              // If it's not a SCRIBENOTE_DEFAULT template, wrap it with PremiumFeatureGate
              if (template.templateType !== "SCRIBENOTE_DEFAULT") {
                return (
                  <PremiumFeatureGate
                    key={template.uuid || template.value}
                    featureName="Adaptive Templates"
                    iconPosition="top-right"
                    iconSize="small"
                    className="!block !w-full !relative"
                    style={{
                      width: "100%",
                      position: "relative",
                      display: "block",
                    }}
                    requiredPermission={
                      UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
                    }
                  >
                    {templateItem}
                  </PremiumFeatureGate>
                );
              }

              return templateItem;
            })}

          {templateType !== "FAVOURITES" && !isModal && (
            <div className="relative">
              <PremiumFeatureGate
                featureName={`New ${
                  templateType.charAt(0) +
                  templateType.slice(1).toLowerCase()
                } Template`}
                iconPosition="top-right"
                iconSize="small"
                requiredPermission={
                  UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
                }
                className="button-premium-gate w-full"
              >
                <button
                  className="w-full flex flex-col items-center justify-center flex-none border-2 border-dashed border-gray-300 dark:border-gray-500 rounded-lg p-4 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                  onClick={handleCreateNewTemplate}
                >
                  <FontAwesomeIcon icon={faPlus} size="lg" />
                </button>
              </PremiumFeatureGate>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoTemplatesList;
